import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Receipt, ArrowRight, Trash2, FileText } from "lucide-react";
import {
  Invoice,
  useCreateInvoice,
  useDeleteInvoice,
  InvoiceStatus,
  useQuoteInvoiceSummary,
} from "@/hooks/useInvoices";
import { useCopyQuoteContentToInvoice } from "@/hooks/useInvoiceEntities";
import { Quote, QuoteVersion } from "@/hooks/useQuotes";
import { formatCurrency } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const STATUS_BADGES: Record<
  InvoiceStatus,
  {
    label: string;
    variant: "default" | "secondary" | "destructive" | "outline";
  }
> = {
  draft: { label: "Draft", variant: "secondary" },
  sent: { label: "Sent", variant: "default" },
  paid: { label: "Paid", variant: "default" },
  cancelled: { label: "Cancelled", variant: "destructive" },
};

function StatusBadge({ status }: { status: InvoiceStatus }) {
  const config = STATUS_BADGES[status];
  return (
    <Badge
      variant={config.variant}
      className={
        status === "paid"
          ? "bg-green-600"
          : status === "sent"
            ? "bg-blue-600"
            : undefined
      }
    >
      {config.label}
    </Badge>
  );
}

interface InvoicesListCardProps {
  projectId: string;
  projectName: string;
  projectNumber?: string;
  invoices: Invoice[];
  quotes: Quote[];
  isLoading: boolean;
  projectDefaultVatRate: "CH_8_1" | "NONE";
  projectDefaultDiscountPercent: number;
  onSelectInvoice: (invoiceId: string) => void;
}

export function InvoicesListCard({
  projectId,
  projectName,
  projectNumber,
  invoices,
  quotes,
  isLoading,
  projectDefaultVatRate,
  projectDefaultDiscountPercent,
  onSelectInvoice,
}: InvoicesListCardProps) {
  const [deleteInvoiceId, setDeleteInvoiceId] = useState<string | null>(null);
  const [fromQuoteDialogOpen, setFromQuoteDialogOpen] = useState(false);
  const [selectedQuoteVersionId, setSelectedQuoteVersionId] = useState<
    string | null
  >(null);
  const [depositPercent, setDepositPercent] = useState<number | undefined>(
    undefined,
  );

  const createInvoice = useCreateInvoice();
  const deleteInvoice = useDeleteInvoice();
  const copyQuoteContent = useCopyQuoteContentToInvoice();

  // Get accepted versions across all quotes
  const acceptedVersions: Array<{
    quote: Quote;
    version: QuoteVersion;
  }> = [];
  quotes.forEach((quote) => {
    quote.versions?.forEach((version) => {
      if (version.is_accepted) {
        acceptedVersions.push({ quote, version });
      }
    });
  });

  const handleCreateBlankInvoice = () => {
    createInvoice.mutate(
      {
        projectId,
        title: projectName,
        projectDiscountPercent: projectDefaultDiscountPercent,
        vatRate: projectDefaultVatRate,
      },
      {
        onSuccess: (invoice) => {
          onSelectInvoice(invoice.id);
        },
      },
    );
  };

  const handleCreateFromQuote = () => {
    if (!selectedQuoteVersionId) return;

    const selected = acceptedVersions.find(
      (v) => v.version.id === selectedQuoteVersionId,
    );
    if (!selected) return;

    createInvoice.mutate(
      {
        projectId,
        title: selected.quote.name,
        sourceQuoteVersionId: selectedQuoteVersionId,
        customerId: selected.quote.customer_id,
        contactId: selected.quote.contact_id,
        depositPercent,
        projectDiscountPercent: selected.version.project_discount_percent,
        vatRate: selected.version.vat_rate as "CH_8_1" | "NONE",
      },
      {
        onSuccess: (invoice) => {
          copyQuoteContent.mutate(
            {
              sourceQuoteVersionId: selectedQuoteVersionId!,
              targetInvoiceId: invoice.id,
            },
            {
              onSuccess: () => {
                setFromQuoteDialogOpen(false);
                setSelectedQuoteVersionId(null);
                setDepositPercent(undefined);
                onSelectInvoice(invoice.id);
              },
            },
          );
        },
      },
    );
  };

  const handleDeleteInvoice = () => {
    if (!deleteInvoiceId) return;
    deleteInvoice.mutate(deleteInvoiceId, {
      onSuccess: () => setDeleteInvoiceId(null),
    });
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("de-CH");
  };

  return (
    <>
      <Card data-testid="invoices-list-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Receipt className="h-5 w-5" />
                Invoices
              </CardTitle>
              <CardDescription>Manage project invoices</CardDescription>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button offlineEdit size="sm" data-testid="new-invoice-button">
                  <Plus className="h-4 w-4 mr-1" />
                  New Invoice
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  offlineEdit
                  onClick={handleCreateBlankInvoice}
                >
                  <Receipt className="h-4 w-4 mr-2" />
                  Blank Invoice
                </DropdownMenuItem>
                {acceptedVersions.length > 0 && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel>From Accepted Quote</DropdownMenuLabel>
                    {acceptedVersions.map(({ quote, version }) => (
                      <DropdownMenuItem
                        offlineEdit
                        key={version.id}
                        onClick={() => {
                          setSelectedQuoteVersionId(version.id);
                          setFromQuoteDialogOpen(true);
                        }}
                        data-testid={`new-invoice-from-quote-${version.id}`}
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        {quote.quote_number} – {quote.name} (v
                        {version.version_number})
                      </DropdownMenuItem>
                    ))}
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : invoices.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Receipt className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-sm">No invoices yet</p>
              <p className="text-xs mt-1">Create an invoice to get started</p>
            </div>
          ) : (
            <div className="space-y-2">
              {invoices.map((invoice) => (
                <div
                  key={invoice.id}
                  className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors group"
                  data-testid={`invoice-row-${invoice.id}`}
                >
                  <div
                    className="flex items-center gap-3 min-w-0 flex-1 cursor-pointer"
                    onClick={() => onSelectInvoice(invoice.id)}
                  >
                    <Receipt className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <div className="min-w-0">
                      <p
                        className="font-medium truncate"
                        data-testid={`invoice-row-title-${invoice.id}`}
                      >
                        {[projectNumber, invoice.invoice_number]
                          .filter(Boolean)
                          .join("-")}{" "}
                        – {invoice.title || "Untitled"}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <StatusBadge status={invoice.status} />
                        <span>{formatDate(invoice.invoice_date)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium tabular-nums">
                      {formatCurrency(invoice.total_gross, invoice.currency)}
                    </span>
                    {invoice.status === "draft" && (
                      <Button
                        offlineEdit
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity text-destructive hover:text-destructive"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteInvoiceId(invoice.id);
                        }}
                        data-testid={`delete-invoice-${invoice.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => onSelectInvoice(invoice.id)}
                    >
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create from Quote Dialog */}
      <Dialog open={fromQuoteDialogOpen} onOpenChange={setFromQuoteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Invoice from Quote</DialogTitle>
            <DialogDescription>
              Create an invoice from the accepted quote content
            </DialogDescription>
          </DialogHeader>

          {selectedQuoteVersionId && (
            <AlreadyBilledSummary quoteVersionId={selectedQuoteVersionId} />
          )}

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Invoice Percentage (optional)</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min={1}
                  max={100}
                  placeholder="100"
                  value={depositPercent ?? ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    setDepositPercent(
                      v === "" ? undefined : parseFloat(v) || undefined,
                    );
                  }}
                  className="w-24"
                />
                <span className="text-muted-foreground">%</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Leave empty for full invoice. Set e.g. 30 for a 30% deposit
                invoice.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setFromQuoteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              offlineEdit
              onClick={handleCreateFromQuote}
              disabled={createInvoice.isPending || copyQuoteContent.isPending}
              data-testid="create-invoice-submit"
            >
              Create Invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog
        open={!!deleteInvoiceId}
        onOpenChange={() => setDeleteInvoiceId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Invoice</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this invoice? This action cannot
              be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteInvoice}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-invoice"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// Shows already-billed summary for a quote version
function AlreadyBilledSummary({ quoteVersionId }: { quoteVersionId: string }) {
  const { data: existingInvoices = [] } =
    useQuoteInvoiceSummary(quoteVersionId);

  if (existingInvoices.length === 0) return null;

  const totalsByCurrency = existingInvoices.reduce<Record<string, number>>(
    (totals, invoice) => ({
      ...totals,
      [invoice.currency]: (totals[invoice.currency] ?? 0) + invoice.total_gross,
    }),
    {},
  );

  return (
    <div className="rounded-md border p-3 bg-muted/50">
      <p className="text-sm font-medium mb-2">Already Billed</p>
      <div className="space-y-1">
        {existingInvoices.map((inv) => (
          <div key={inv.id} className="flex justify-between text-sm">
            <span className="text-muted-foreground">
              #{inv.invoice_number} – {inv.title}
            </span>
            <span className="tabular-nums font-medium">
              {formatCurrency(inv.total_gross, inv.currency)}
            </span>
          </div>
        ))}
        <div className="flex justify-between text-sm font-medium border-t pt-1 mt-1">
          <span>Total Billed</span>
          <span className="tabular-nums text-right">
            {Object.entries(totalsByCurrency).map(([currency, total]) => (
              <span key={currency} className="block">
                {formatCurrency(total, currency)}
              </span>
            ))}
          </span>
        </div>
      </div>
    </div>
  );
}
